import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../services/api';
import './TemplateUsagesModal.css';

function TemplateUsagesModal({ isOpen, templateId, templateName, onClose }) {
    const navigate = useNavigate();
    const [usages, setUsages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && templateId) {
            loadUsages();
        }
    }, [isOpen, templateId]);

    const loadUsages = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await API.getTemplateUsages(templateId);
            setUsages(response.data.usages || []);
        } catch (err) {
            console.error('Failed to load template usages:', err);
            setError('Erreur lors du chargement des utilisations');
        } finally {
            setLoading(false);
        }
    };

    const handleSignatureClick = (signatureId) => {
        navigate(`/builder?id=${signatureId}`);
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="template-usages-modal-overlay" onClick={handleOverlayClick}>
            <div className="template-usages-modal-content">
                <div className="template-usages-modal-header">
                    <div>
                        <h2>Utilisations du modèle</h2>
                        <p className="template-usages-modal-subtitle">
                            {templateName}
                        </p>
                    </div>
                    <button className="template-usages-modal-close" onClick={onClose}>
                        <svg
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="template-usages-modal-body">
                    {loading ? (
                        <div className="usages-loading">
                            <div className="spinner"></div>
                            <p>Chargement...</p>
                        </div>
                    ) : error ? (
                        <div className="usages-error">
                            <p>{error}</p>
                        </div>
                    ) : usages.length === 0 ? (
                        <div className="usages-empty">
                            <svg
                                width="48"
                                height="48"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                />
                            </svg>
                            <h3>Aucune utilisation</h3>
                            <p>Ce modèle n'est utilisé dans aucune signature</p>
                        </div>
                    ) : (
                        <div className="usages-list">
                            <p className="usages-count">
                                Ce modèle est utilisé dans {usages.length} signature{usages.length > 1 ? 's' : ''}
                            </p>
                            {usages.map((usage) => (
                                <div
                                    key={usage.signature_id}
                                    className="usage-item"
                                    onClick={() => handleSignatureClick(usage.signature_id)}
                                >
                                    <div className="usage-info">
                                        <svg
                                            width="20"
                                            height="20"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <div className="usage-details">
                                            <h4>{usage.signature_name}</h4>
                                            <p className="usage-meta">
                                                {usage.is_locked ? (
                                                    <span className="usage-locked">
                                                        <svg
                                                            width="14"
                                                            height="14"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                            />
                                                        </svg>
                                                        Verrouillé
                                                    </span>
                                                ) : (
                                                    <span className="usage-unlocked">
                                                        <svg
                                                            width="14"
                                                            height="14"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        Déverrouillé
                                                    </span>
                                                )}
                                                <span className="usage-version">
                                                    Version {usage.version}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <svg
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="template-usages-modal-footer">
                    <button className="template-usages-close-btn" onClick={onClose}>
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TemplateUsagesModal;
